import { Link } from "react-router-dom";
import { Building2, KeyRound, Server } from "lucide-react";
import { StatCard } from "@/components/StatCard";
import { SectionCard } from "@/components/SectionCard";
import { Badge } from "@/components/ui/badge";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { ListTableShell } from "@/components/ResourceShell";
import { useFleet } from "@/api/queries/fleet";
import { useCustomers } from "@/api/queries/customers";
import { useLicenses } from "@/api/queries/licenses";
import { relativeTime } from "@/lib/relativeTime";
import type { Customer } from "@/types";

const STALE_MS = 48 * 60 * 60 * 1000;

export function Dashboard() {
  const fleet = useFleet();
  const customers = useCustomers();
  const licenses = useLicenses();

  const customerById = new Map<string, Customer>((customers.data ?? []).map((c) => [c.id, c]));
  const reports = fleet.data ?? [];

  return (
    <div className="space-y-6">
      <div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
        <StatCard to="/customers" icon={Building2} label="Kunden" value={customers.data?.length ?? "—"} tone="neutral" />
        <StatCard to="/licenses" icon={KeyRound} label="Lizenzen" value={licenses.data?.length ?? "—"} tone="neutral" />
        <StatCard to="/" icon={Server} label="Deployments (gemeldet)" value={reports.length} tone="neutral" />
      </div>

      <SectionCard title="Fleet" description="Juengster Nutzungs-Report pro Deployment" noPad>
        <ListTableShell
          isLoading={fleet.isLoading}
          isError={fleet.isError}
          errorMessage={fleet.error?.message}
          isEmpty={reports.length === 0}
          emptyMessage="Noch keine Usage-Reports"
          emptyHint="Deployments melden Nutzung ueber /v1/usage."
          onRetry={() => fleet.refetch()}
        >
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Deployment</TableHead>
                <TableHead>Kunde</TableHead>
                <TableHead>Agents</TableHead>
                <TableHead>Users</TableHead>
                <TableHead>Nodes</TableHead>
                <TableHead>Channels</TableHead>
                <TableHead>Gemeldet</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {reports.map((r) => {
                const stale = Date.now() - new Date(r.serverTime).getTime() > STALE_MS;
                const customer = customerById.get(r.customerId);
                return (
                  <TableRow key={r.deploymentId} data-clickable="true">
                    <TableCell className="mono text-xs">
                      <Link to={`/licenses/${r.licenseId}`} className="text-accent hover:underline">
                        {r.deploymentId}
                      </Link>
                    </TableCell>
                    <TableCell>{customer ? customer.name : <span className="mono text-xs text-text3">{r.customerId}</span>}</TableCell>
                    <TableCell className="tnum">{r.agents}</TableCell>
                    <TableCell className="tnum">{r.users}</TableCell>
                    <TableCell className="tnum">{r.nodes}</TableCell>
                    <TableCell className="tnum">{r.channels}</TableCell>
                    <TableCell>
                      <span className="flex items-center gap-2">
                        {relativeTime(r.serverTime)}
                        {stale && <Badge variant="busy" data-testid="stale-badge">stale</Badge>}
                      </span>
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </ListTableShell>
      </SectionCard>
    </div>
  );
}
